import { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Route, Routes } from "react-router-dom";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RootErrorBoundary, RouteErrorBoundary } from "@/components/error-boundary";
import { PageSkeleton } from "@/components/page-skeleton";
import { Toaster } from "@/components/ui/toast";
import { TooltipProvider } from "@/components/ui/tooltip";
import LoginPage from "@/pages/auth/LoginPage";
import AuditLogPage from "@/pages/admin/audit/AuditLogPage";
import CallLogPage from "@/pages/admin/calls/CallLogPage";
import CategoriesPage from "@/pages/admin/categories/CategoriesPage";
import AdminFlowsPage from "@/pages/admin/flows/AdminFlowsPage";
import FlowBuilderPage from "@/pages/admin/flows/FlowBuilderPage";
import AdminGuidesPage from "@/pages/admin/guides/AdminGuidesPage";
import StaffFormPage from "@/pages/admin/staff/StaffFormPage";
import StaffListPage from "@/pages/admin/staff/StaffListPage";
import FlowListPage from "@/pages/staff/flows/FlowListPage";
import FlowPlayerPage from "@/pages/staff/flows/FlowPlayerPage";
import WizardFlowPlayerPage from "@/pages/staff/flows/WizardFlowPlayerPage";
import FlowWizardPage from "@/pages/admin/flows/FlowWizardPage";
import GuideListPage from "@/pages/staff/guides/GuideListPage";
import { useMe } from "@/hooks/api/auth";
import { useAuthStore } from "@/stores/auth";

export default function App() {
  const hydrate = useAuthStore((s) => s.hydrate);
  const languagePreference = useAuthStore((s) => s.user?.language_preference);
  const { i18n } = useTranslation();

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // `hydrate()` only has the JWT to go on, so it seeds `user` with a
  // "BOTH" language_preference placeholder (see stores/auth.ts) on every
  // fresh page load — a stored token doesn't carry the real value. This
  // backfills it from the server once we're actually authenticated;
  // without it, a Staff member's real preference (and anything derived
  // from it, like which language fields FlowPlayerPage's personalization
  // editor shows) would silently stay wrong until their next login.
  useMe();

  // UI chrome language follows the signed-in user's preference. "BOTH" has
  // no sensible interface-language reading (you can't show two languages of
  // one button label at once) so it defaults the chrome to English — flow
  // *content* still renders in both languages regardless, see FlowPlayerPage.
  useEffect(() => {
    if (!languagePreference) return;
    const lng = languagePreference === "ES" ? "es" : "en";
    if (i18n.language !== lng) {
      i18n.changeLanguage(lng);
    }
  }, [languagePreference, i18n]);

  return (
    <RootErrorBoundary>
      <TooltipProvider delayDuration={300}>
        <RouteErrorBoundary>
          <Suspense fallback={<PageSkeleton />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              {/* Browse & play published flows — every authenticated role
                  (Admin can preview/play too; the backend's IsStaff gate
                  already passes Admins). */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <FlowListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/flows/:id/play"
                element={
                  <ProtectedRoute>
                    <FlowPlayerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/flows"
                element={
                  <ProtectedRoute roles={["ADMIN"]}>
                    <AdminFlowsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/flows/:id/builder"
                element={
                  <ProtectedRoute roles={["ADMIN"]}>
                    <FlowBuilderPage />
                  </ProtectedRoute>
                }
              />
              {/* Wizard-based flows — new simplified approach (isolated testing) */}
              <Route
                path="/wizard-flows/create"
                element={
                  <ProtectedRoute roles={["ADMIN"]}>
                    <FlowWizardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wizard-flows/:id"
                element={
                  <ProtectedRoute roles={["ADMIN"]}>
                    <FlowWizardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wizard-flows/:id/play"
                element={
                  <ProtectedRoute>
                    <WizardFlowPlayerPage />
                  </ProtectedRoute>
                }
              />
              {/* Guides — FAQs / difficult-caller reference content not
                  covered by a Script. Browse is open to every authenticated
                  role (like FlowListPage); management is Admin-only. */}
              <Route
                path="/guides"
                element={
                  <ProtectedRoute>
                    <GuideListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/guides/manage"
                element={
                  <ProtectedRoute roles={["ADMIN"]}>
                    <AdminGuidesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff"
                element={
                  <ProtectedRoute roles={["ADMIN"]}>
                    <StaffListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/new"
                element={
                  <ProtectedRoute roles={["ADMIN"]}>
                    <StaffFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/categories"
                element={
                  <ProtectedRoute roles={["ADMIN"]}>
                    <CategoriesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/call-log"
                element={
                  <ProtectedRoute roles={["ADMIN"]}>
                    <CallLogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/audit-log"
                element={
                  <ProtectedRoute roles={["ADMIN"]}>
                    <AuditLogPage />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<LoginPage />} />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
        <Toaster />
      </TooltipProvider>
    </RootErrorBoundary>
  );
}
